import type { Question } from './contract'
import { textFormatHint, textFormatValid } from './question-type'

// 一題 `date` 實際問到多細。
//
// 題型只有 `date` 一種，但題庫裡有些題目其實只問年份（「請問您的出生年份（西元）？」）。
// 拿日期選單去問它，讀者得多挑兩段沒有意義的月日 —— 而那兩段最後也只是被丟掉的雜訊。
//
// 這一層只改「畫面上問什麼、值長什麼樣」；送出與保存的形狀不變（見下方 serialize），
// 所以 contract.ts 一個字都不用動。

/**
 * 一題 date 問到多細。目前只有 `'year'`；未來若出現只問月日之類的題目，加一個值、
 * 在 PRECISION_SPECS 補一列。
 *
 * 沒登記在 PRECISION_BY_ID 的 date 題一律是完整日期 —— 這是安全的預設：漏登記的後果
 * 是那一題退回日期選單，功能與送出的資料都仍然合法。
 */
export type DatePrecision = 'year'

/**
 * 哪一題用哪一種 precision。**靠寫死的 question id 認題**。
 *
 * ⚠️ 同一題在不同環境是不同的 UUID，所以每一題都要把各環境的 id 都列進來；只列一個
 * 環境的話，另一個環境會安靜地退回日期選單。
 *
 * 這不是過渡方案 —— 在題庫題上加一個穩定欄位（code／precision 之類）的路已經提過並被
 * 回絕，理由是這種區分不該進資料型別。所以下一題要特化時照樣往這裡加 id，不必再問一次。
 *
 * id 會漂：題庫題被刪掉重建就換一個新的 UUID，而本 repo 沒有任何東西會因此變紅 ——
 * 那份清單的 owner 不在這裡，本 repo 也沒有自動跟它對帳的路。漂掉的徵狀是那一題變回
 * 日期選單（功能與送出的資料都仍然合法）；修法是重撈一次題庫清單，把新 id 補進來。
 */
const PRECISION_BY_ID: Record<string, DatePrecision> = {
  // 「請問您的出生年份（西元）？」
  'b3fd8b8d-02ed-4577-91d6-2f60ad5eb84d': 'year', // staging
  '4e64e4b4-9881-49a0-bd47-bce5fb8981fe': 'year', // production
}

// 讀者可能填得出的最老出生年份。動態算而不是寫死一個年份：寫死的下限會隨時間變成一個
// 沒人記得為什麼是它的數字，而這題問的是活著的人的出生年。
const MAX_AGE_YEARS = 120

// 合法的年份區間。valid 與 hint 共用它 —— 擋下的規則與說出口的理由必須是同一組數字，
// 否則畫面會擋一個區間、卻叫讀者去填另一個。
function yearRange(): { min: number, max: number } {
  const now = new Date().getFullYear()
  return { min: now - MAX_AGE_YEARS, max: now }
}

/**
 * 一種 precision 的行為：畫面怎麼收、什麼算合法、以及它與 wire 形狀之間怎麼換算。
 *
 * serialize／deserialize 是一對：state 裡放讀者實際打的字（年份題就是四位數），送出與
 * 存進 localStorage 前一律換成完整日期。這樣 prefill 讀回舊值時仍用題型自己的格式規則
 * 重驗即可，不必知道 precision 這回事。
 */
interface PrecisionSpec {
  /** 輸入框收得下幾個字。 */
  maxlength: number
  /** 題目自己沒帶 placeholder 時用的預設。 */
  placeholder: string
  /** 非空的值是否合法（空值不歸這裡管，見 state.ts 的 answerGap）。 */
  valid: (text: string) => boolean
  /** 擋下時說的那句話。做成函式是因為話裡帶著動態算出來的區間。 */
  hint: () => string
  /** 畫面上的值 → 送出與保存的完整日期。 */
  serialize: (text: string) => string
  /** 送出與保存的完整日期 → 畫面上的值。 */
  deserialize: (wire: string) => string
}

const PRECISION_SPECS: Record<DatePrecision, PrecisionSpec> = {
  year: {
    maxlength: 4,
    placeholder: '例：1990',
    // 四位數字，且落在「活著的人的出生年」這個區間內。上下限都動態算，所以這條規則不會
    // 隨著時間過期。
    valid: (text) => {
      if (!/^\d{4}$/.test(text))
        return false
      const year = Number(text)
      const { min, max } = yearRange()
      return year >= min && year <= max
    },
    // 一條規則有兩個失敗成因（位數不對、超出區間），所以話得同時說中兩者。只說「請輸入
    // 4 位數字」的話，填了 1800 的讀者盯著自己打的四位數字會無路可走。
    hint: () => {
      const { min, max } = yearRange()
      return `請輸入西元年份（${min}–${max}）`
    },
    // 送出的欄位仍是完整日期（見 question-type.ts 的 date 那列），月日補該年的第一天。
    // 這一題語意上只問年，補出來的月日不帶資訊。
    serialize: text => `${text}-01-01`,
    // localStorage 裡可能躺著這題還是日期選單時存下的完整日期。取年份、丟掉月日 ——
    // 那兩段當初是被選單逼出來的，不是讀者想說的話。
    deserialize: wire => wire.slice(0, 4),
  },
}

/** 這一題問到多細；沒登記（或根本不是 date 題）回 undefined。 */
export function datePrecision(q: Pick<Question, 'id' | 'type'>): DatePrecision | undefined {
  // 一併看題型：id 是寫死的，而題庫題可以被刪掉重建。一個曾經是年份題的 id 若被指到別的
  // 題型上，認題就該失效，而不是把年份輸入框畫到一個非日期題上。
  return q.type === 'date' ? PRECISION_BY_ID[String(q.id)] : undefined
}

/** 年份輸入框要用的屬性；不是年份這類窄題目回 undefined。 */
export function precisionInput(q: Pick<Question, 'id' | 'type'>): Pick<PrecisionSpec, 'maxlength' | 'placeholder'> | undefined {
  const p = datePrecision(q)
  if (p === undefined)
    return undefined
  const { maxlength, placeholder } = PRECISION_SPECS[p]
  return { maxlength, placeholder }
}

/**
 * 讀者當下打在畫面上的值合不合格式。**驗使用者輸入一律走這裡**，不要直接用
 * question-type.ts 的 textFormatValid —— 那一支只認題型，會拿完整日期的規則去驗一個
 * 四位數年份，然後把每一筆年份都判成格式錯誤。
 */
export function answerFormatValid(q: Pick<Question, 'id' | 'type'>, text: string): boolean {
  const p = datePrecision(q)
  return p !== undefined ? PRECISION_SPECS[p].valid(text) : textFormatValid(q.type, text)
}

/** 格式不符時要說的那句話。與 answerFormatValid 同源：擋下的規則與說出口的理由是同一件事。 */
export function answerFormatHint(q: Pick<Question, 'id' | 'type'>): string {
  const p = datePrecision(q)
  return p !== undefined ? PRECISION_SPECS[p].hint() : textFormatHint(q.type)
}

/** 畫面上的值 → 送出與存進 localStorage 的形狀。沒有 precision 的題目原樣通過。 */
export function serializeDateAnswer(q: Pick<Question, 'id' | 'type'>, text: string): string {
  const p = datePrecision(q)
  return p !== undefined ? PRECISION_SPECS[p].serialize(text) : text
}

/** 送出與 localStorage 的形狀 → 畫面上的值。沒有 precision 的題目原樣通過。 */
export function deserializeDateAnswer(q: Pick<Question, 'id' | 'type'>, wire: string): string {
  const p = datePrecision(q)
  return p !== undefined ? PRECISION_SPECS[p].deserialize(wire) : wire
}
